import { FC, useCallback, useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useIsFetching } from 'react-query';
import { ActiveAuthorizePaymentPage, EVENT_NAME, Tabs } from 'common/constants';
import { useAmplitude } from 'components/AmplitudeProvider';
import { useClient } from 'components/ClientProvider';
import { setErrorMessages } from 'components/ErrorMessageHandler/slice';
import { getNoStoreAvailable } from 'components/StoreSelect/slice';
import { useFlagValue } from 'hooks';
import { clearSelectedReconcileRecords } from 'pages/ReconciliationReportPage/slice';
import { getActiveAuthorizePaymentPage } from 'pages/VendorPaymentResultPage/selectors';
import { updateActiveAuthorizePaymentPage } from 'pages/VendorPaymentResultPage/slice';
import {
  clearPostingDescription,
  errors,
  getPaymentDate,
  getSelectedVendors
} from 'pages/VendorsTablePage/components/PrintChecks';

import {
  clearSelectedVendors,
  getOpenGlCalendar,
  getSelectedCIBAccount,
  getIsLoadingPrintChecks,
  setIsPrintChecksDialogOpen
} from 'pages/VendorsTablePage/components/PrintChecks/slice';
import {
  clearSelectedInvoices,
  getDueDate,
  getIsLoadingVendors,
  getPaymentMethods,
  getSelectedVendorInvoicePairs
} from 'pages/VendorsTablePage/components/VendorsTable/slice';
import { useDeclinePayment } from 'pages/VendorsTablePage/hooks/useDeclinePayment';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { amplitudeTracker } from 'utils/AmplitudeTracker';
import AuthorizeScreenFlow from './AuthorizeScreenFlow';
import { getSelectedTab } from './components/LandingPageHeader/slice';
import { getValidPostingRequirements } from './getValidPostingRequirements';
import { checkIfPaymentDateIsSameOrAfterPostaheadStart } from './helpers';
import { messages } from './messages';
import { getUserRole } from './slice';

const LandingPageContent: FC = () => {
  const dispatch = useAppDispatch();
  const client = useClient();
  const amplitude = useAmplitude();
  const isFetchingPrintChecks = useIsFetching('printAuthorizedVendors');
  const intl = useIntl();
  const activeAuthorizePage: string = useAppSelector(
    getActiveAuthorizePaymentPage
  );
  const dueDate = useAppSelector(getDueDate);
  const paymentMethods = useAppSelector(getPaymentMethods);
  const selectedTab = useAppSelector(getSelectedTab);
  const selectedCIBAccount = useAppSelector(getSelectedCIBAccount);
  const selectedVendorInvoicePairs = useAppSelector(
    getSelectedVendorInvoicePairs
  );
  const paymentDate = useAppSelector(getPaymentDate);
  const selectedPrintChecks = useAppSelector(getSelectedVendors);
  const dates = useAppSelector(getOpenGlCalendar);
  const userRole = useAppSelector(getUserRole);
  const noStoreAvailable = useAppSelector(getNoStoreAvailable);
  const { declinePayment, isLoading: isDeclinePaymentLoading } =
    useDeclinePayment();
  const isLoadingPrintChecks = useAppSelector(getIsLoadingPrintChecks);
  const isLoadingVendors = useAppSelector(getIsLoadingVendors);
  const interCompanyFlag = useFlagValue('dmsAccountingIntercompanyPayment');
  const landingPageFlag = useFlagValue('dmsAccountingLandingPage');

  useEffect(() => {
    dispatch(clearSelectedInvoices());
  }, [dispatch, dueDate, paymentMethods, selectedTab]);

  useEffect(() => {
    dispatch(clearSelectedVendors());
    dispatch(clearSelectedReconcileRecords());
  }, [dispatch, selectedTab]);

  useEffect(() => {
    if (noStoreAvailable) {
      dispatch(
        setErrorMessages([intl.formatMessage(messages.noStoreAvailable)])
      );
    }
  }, [dispatch, noStoreAvailable, intl, selectedTab]);

  const handleDeclineButtonClick = useCallback(() => {
    declinePayment(selectedVendorInvoicePairs);
    amplitudeTracker(amplitude, EVENT_NAME.DeclinePayment);
  }, [amplitude, declinePayment, selectedVendorInvoicePairs]);

  const handleReviewButtonClick = useCallback(() => {
    dispatch(
      updateActiveAuthorizePaymentPage(
        ActiveAuthorizePaymentPage.AuthorizeReview
      )
    );
    if (selectedTab === Tabs.ToBePaid)
      amplitudeTracker(amplitude, EVENT_NAME.SelectVendorInvoices);
    else amplitudeTracker(amplitude, EVENT_NAME.ReviewVendorSelections);
  }, [amplitude, dispatch, selectedTab]);

  const handleReviewAndPrintButton = () => {
    if (!selectedPrintChecks.length) {
      dispatch(setErrorMessages([intl.formatMessage(errors.noChecks)]));
    } else {
      dispatch(
        updateActiveAuthorizePaymentPage(
          ActiveAuthorizePaymentPage.ReviewChecks
        )
      );
    }
  };

  const handlePrintCheckButtonClick = () => {
    if (selectedCIBAccount?.index === -1) {
      dispatch(
        setErrorMessages([intl.formatMessage(errors.noCIBAccountSelected)])
      );
    } else if (!selectedPrintChecks.length) {
      dispatch(setErrorMessages([intl.formatMessage(errors.noChecks)]));
    } else if (!paymentDate) {
      dispatch(
        setErrorMessages([intl.formatMessage(errors.paymentDateRequired)])
      );
    } else if (
      !getValidPostingRequirements(dates, paymentDate, userRole) &&
      !checkIfPaymentDateIsSameOrAfterPostaheadStart(
        dates.postaheadStart,
        paymentDate
      )
    ) {
      dispatch(
        setErrorMessages([intl.formatMessage(errors.paymentDateInvalid)])
      );
    } else {
      dispatch(clearPostingDescription());
      dispatch(setIsPrintChecksDialogOpen(true));
    }
  };

  const isLoading = useMemo(() => {
    switch (selectedTab) {
      case Tabs.NeedsAuthorization:
      case Tabs.ToBePaid:
        return isLoadingVendors;
      case Tabs.PrintChecks:
        return isLoadingPrintChecks;
      default:
        return false;
    }
  }, [isLoadingPrintChecks, isLoadingVendors, selectedTab]);

  return (
    <AuthorizeScreenFlow
      activeAuthorizePage={activeAuthorizePage}
      client={client}
      selectedTab={selectedTab}
      isFetchingPrintChecks={isFetchingPrintChecks}
      isLoading={isLoading}
      interCompanyFlag={interCompanyFlag}
      landingPageFlag={landingPageFlag}
      handleDeclineButtonClick={handleDeclineButtonClick}
      handleReviewButtonClick={handleReviewButtonClick}
      handleReviewAndPrintButton={handleReviewAndPrintButton}
      handlePrintCheckButtonClick={handlePrintCheckButtonClick}
      isDeclinePaymentLoading={isDeclinePaymentLoading}
    />
  );
};

export default LandingPageContent;
